// Forudgenereret diktat-lyd fra public/audio/da/ og public/audio/en/.
// Falder tilbage til Web Speech for ord uden mp3.

import { audioSlug } from './audioSlug';
import type {
  DictationAudio,
  DictationAudioLocale,
  DictationSpeakOptions,
  DictationVoice,
} from './dictationAudio';
import { WebSpeechAudio } from './webSpeechAudio';

const MANIFESTS = {
  da: { url: '/audio/da/manifest.json', base: '/audio/da' },
  en: { url: '/audio/en/manifest.json', base: '/audio/en' },
} as const;

const PRERENDERED_VOICE_IDS = {
  da: '__prerendered_da__',
  en: '__prerendered_en__',
} as const;

interface AudioManifest {
  version?: number;
  locale?: string;
  voiceId?: string;
  modelId?: string;
  /** word → filename (e.g. "ord.mp3") */
  files: Record<string, string>;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

export class PrerenderedAudio implements DictationAudio {
  private manifests: Partial<Record<'da' | 'en', AudioManifest>> = {};
  private manifestReady: Promise<void>;
  private current: HTMLAudioElement | null = null;
  private readonly fallback = new WebSpeechAudio();
  private readonly cache = new Map<string, HTMLAudioElement>();

  constructor() {
    this.manifestReady = this.loadManifests();
  }

  private async loadManifests(): Promise<void> {
    await Promise.all(
      (Object.keys(MANIFESTS) as Array<'da' | 'en'>).map(async (locale) => {
        try {
          const { url } = MANIFESTS[locale];
          const res = await fetch(url);
          if (!res.ok) return;
          const data = (await res.json()) as AudioManifest;
          if (data?.files && typeof data.files === 'object') {
            this.manifests[locale] = data;
          }
        } catch {
          /* offline or missing manifest */
        }
      }),
    );
  }

  private hasPrerenderedFiles(locale: 'da' | 'en'): boolean {
    const m = this.manifests[locale];
    return !!m && Object.keys(m.files).length > 0;
  }

  private lookupLocales(
    word: string,
    audioLocale: DictationAudioLocale,
  ): Array<'da' | 'en'> {
    void word;
    if (audioLocale === 'da') return ['da'];
    if (audioLocale === 'en') return ['en'];
    return ['da', 'en'];
  }

  private fileForWord(
    word: string,
    audioLocale: DictationAudioLocale,
  ): { locale: 'da' | 'en'; file: string } | null {
    const key = word.trim();
    if (!key) return null;
    for (const locale of this.lookupLocales(key, audioLocale)) {
      const file = this.manifests[locale]?.files[key];
      if (file) return { locale, file };
    }
    return null;
  }

  private urlForWord(
    word: string,
    audioLocale: DictationAudioLocale,
  ): string | null {
    const hit = this.fileForWord(word, audioLocale);
    if (!hit) return null;
    return `${MANIFESTS[hit.locale].base}/${hit.file}`;
  }

  isAvailable(): boolean {
    if (this.hasPrerenderedFiles('da') || this.hasPrerenderedFiles('en')) {
      return true;
    }
    return this.fallback.isAvailable();
  }

  waitForVoices(): Promise<void> {
    return Promise.all([
      this.manifestReady,
      this.fallback.waitForVoices(),
    ]).then(() => undefined);
  }

  listDanishVoices(): DictationVoice[] {
    const out: DictationVoice[] = [];
    if (this.hasPrerenderedFiles('da')) {
      out.push({
        id: PRERENDERED_VOICE_IDS.da,
        name: 'Forudgenereret dansk',
        lang: 'da-DK',
        localService: true,
      });
    }
    if (this.hasPrerenderedFiles('en')) {
      out.push({
        id: PRERENDERED_VOICE_IDS.en,
        name: 'Forudgenereret engelsk',
        lang: 'en-US',
        localService: true,
      });
    }
    if (out.length > 0) return out;
    return this.fallback.listDanishVoices();
  }

  cancel(): void {
    if (this.current) {
      try {
        this.current.pause();
        this.current.currentTime = 0;
      } catch {
        /* ignore */
      }
      this.current = null;
    }
    this.fallback.cancel();
  }

  async preload(texts: string[], opts: DictationSpeakOptions = {}): Promise<void> {
    const audioLocale = opts.audioLocale ?? 'da';
    const keys = new Set<string>();
    for (const t of texts) {
      const trimmed = t.trim();
      if (trimmed) keys.add(trimmed);
      for (const w of trimmed.split(/\s+/)) {
        if (w) keys.add(w);
      }
    }
    await Promise.all(
      [...keys].map((k) =>
        this.prefetchWord(k, audioLocale).catch(() => undefined),
      ),
    );
  }

  private cacheKey(word: string, url: string): string {
    return `${url}::${word}`;
  }

  private async prefetchWord(
    word: string,
    audioLocale: DictationAudioLocale,
  ): Promise<void> {
    const url = this.urlForWord(word, audioLocale);
    if (!url) return;
    const ck = this.cacheKey(word, url);
    if (this.cache.has(ck)) return;
    const audio = new Audio(url);
    audio.preload = 'auto';
    this.cache.set(ck, audio);
    await new Promise<void>((resolve, reject) => {
      audio.oncanplaythrough = () => resolve();
      audio.onerror = () => reject(new Error('preload failed'));
    });
  }

  speak(text: string, opts: DictationSpeakOptions = {}): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed) return Promise.resolve();

    const audioLocale = opts.audioLocale ?? 'da';
    const url = this.urlForWord(trimmed, audioLocale);
    if (url) {
      return this.playUrl(trimmed, url, opts.rate ?? 0.85, audioLocale, opts);
    }

    return this.speakWithFallback(trimmed, audioLocale, opts);
  }

  private speakWithFallback(
    text: string,
    audioLocale: DictationAudioLocale,
    opts: DictationSpeakOptions,
  ): Promise<void> {
    const fallbackLocale =
      audioLocale === 'auto' ? 'da' : audioLocale === 'en' ? 'en' : 'da';
    return this.fallback.speak(text, {
      ...opts,
      audioLocale: fallbackLocale,
    });
  }

  private playUrl(
    word: string,
    url: string,
    rate: number,
    audioLocale: DictationAudioLocale,
    opts: DictationSpeakOptions,
  ): Promise<void> {
    this.cancel();
    const playbackRate = clamp(rate, 0.5, 1.4);
    const ck = this.cacheKey(word, url);

    let audio = this.cache.get(ck);
    if (!audio || audio.src !== new URL(url, window.location.origin).href) {
      audio = new Audio(url);
      this.cache.set(ck, audio);
    }

    this.current = audio;
    audio.playbackRate = playbackRate;

    return new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        if (this.current === audio) this.current = null;
        resolve();
      };
      const tryFallback = () => {
        if (settled) return;
        settled = true;
        if (this.current === audio) this.current = null;
        void this.speakWithFallback(word, audioLocale, { ...opts, rate }).then(
          resolve,
        );
      };
      audio!.onended = finish;
      audio!.onerror = tryFallback;
      try {
        audio!.currentTime = 0;
        const p = audio!.play();
        if (p) void p.catch(tryFallback);
      } catch {
        tryFallback();
      }
    });
  }
}

/** Slug map for build tooling (word → expected filename). */
export function dictationAudioFilename(word: string): string {
  return `${audioSlug(word)}.mp3`;
}
